import React from 'react';
import PropTypes from 'prop-types';
import { FiAlertTriangle, FiCalendar, FiCheckCircle, FiClock } from 'react-icons/fi';

// One place for the four shipment states the Logistic dashboard, the KPI
// drawers and the Tracking grid all speak. Mirrors
// Crm::Logistic::TrackingSerializer#tracking_status on the backend.
export const STATUS_LABELS = {
  shipped: 'Shipped',
  delayed: 'Delayed',
  pending_booking: 'Pending booking',
  booked_pending: 'Booked, pending',
};

export const STATUS_ICONS = {
  shipped: FiCheckCircle,
  delayed: FiAlertTriangle,
  pending_booking: FiClock,
  booked_pending: FiCalendar,
};

export const STATUS_TONES = {
  shipped: 'tw-bg-emerald-50 tw-text-emerald-700',
  delayed: 'tw-bg-amber-50 tw-text-amber-700',
  pending_booking: 'tw-bg-slate-100 tw-text-slate-600',
  booked_pending: 'tw-bg-sky-50 tw-text-sky-700',
};

// What each state actually means, in the same file as the labels so the legend
// cannot describe a rule the code no longer applies. Mirrors
// Crm::Logistic::TrackingSerializer#tracking_status and DashboardService#detail.
export const STATUS_DESCRIPTIONS = {
  shipped: 'Actual ETD recorded, and not later than the Booking ETD.',
  delayed: 'Actual ETD is later than the Booking ETD.',
  booked_pending: 'Booking released, but no Actual ETD recorded yet.',
  pending_booking: 'No booking released and no Actual ETD yet.',
};

// 'shipped' means on time on a row badge, but "departed at all" as a filter. The
// legend is the one place that has to say so out loud.
export const STATUS_FILTER_NOTE = '“Departed” in the Status filter returns both on-time and delayed shipments; the badge on a row says “Shipped” only when it left on time.';

export const DELAY_NOTES = [
  ['vs Booking', 'Actual ETD minus Booking ETD — did it leave later than the slot that was booked.'],
  ['vs CSD', 'Actual ETD minus CSD request ETD — did it leave later than the date CSD promised the customer.'],
  ['—', 'Not known yet: no Actual ETD, or nothing booked. Never shown as 0, which would read as on time.'],
];

// Chart colours, kept next to the labels so the donut legend and the badges
// cannot drift apart.
export const STATUS_CHART_COLORS = {
  on_time: '#2563eb',
  delayed: '#f59e0b',
  pending_booking: '#94a3b8',
  booked_pending: '#0891b2',
};

// `tracking_status` comes from the serializer, but older cached rows may not carry
// it. One copy of the fallback rule, mirroring
// Crm::Logistic::TrackingSerializer#tracking_status.
export const deriveTrackingStatus = (row) => {
  if (!row) return 'pending_booking';
  if (row.tracking_status) return row.tracking_status;
  if (row.actual_etd) return Number(row.delay_days) > 0 ? 'delayed' : 'shipped';
  return row.release_booking?.id ? 'booked_pending' : 'pending_booking';
};

export const StatusPill = ({ status, className = '' }) => {
  const Icon = STATUS_ICONS[status] || FiClock;
  return (
    <span className={`tw-inline-flex tw-items-center tw-gap-1 tw-rounded tw-px-1.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-whitespace-nowrap ${STATUS_TONES[status] || STATUS_TONES.pending_booking} ${className}`}>
      <Icon size={11} aria-hidden="true" />
      {STATUS_LABELS[status] || status}
    </span>
  );
};

StatusPill.propTypes = { status: PropTypes.string, className: PropTypes.string };

export default StatusPill;
